// What the run used against what its parts list said, line by line. A short
// line is tinted and says how many in words. Money shows only when the API sent
// it (financial users), as a sentence under the table.

import React, { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/table/DataTable';
import { formatTimestamp } from '../utils/date-utils';
import { Alert } from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import { Skeleton } from '../components/ui/Skeleton';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import Tag from '../components/ui/Tag';
import {
  formatCents,
  formatQty,
  useWorkOrdersApi,
  workOrderErrorMessage,
  type CloseReport,
  type CloseReportLine,
} from './workOrdersApi';

const num = (id: keyof CloseReportLine, header: string): ColumnDef<CloseReportLine, any> => ({
  id: String(id),
  header,
  accessorFn: (l: CloseReportLine) => Number(l[id]),
  meta: { align: 'right', numeric: true },
});

/** Used, split by built and flagged drones. Shown only when the API sends the split. */
const SPLIT_COLUMNS: ColumnDef<CloseReportLine, any>[] = [
  {
    id: 'consumed_completed',
    header: 'On built',
    accessorFn: (l: CloseReportLine) => l.consumed_completed ?? 0,
    meta: { align: 'right', numeric: true },
  },
  {
    id: 'consumed_flagged',
    header: 'On flagged',
    accessorFn: (l: CloseReportLine) => l.consumed_flagged ?? 0,
    meta: { align: 'right', numeric: true },
    cell: ({ row }) =>
      (row.original.consumed_flagged ?? 0) > 0 ? (
        <span className="text-orange-300">{row.original.consumed_flagged}</span>
      ) : (
        <span className="text-on-surface-variant">0</span>
      ),
  },
];

const COLUMNS: ColumnDef<CloseReportLine, any>[] = [
  {
    id: 'target',
    header: 'Part',
    accessorFn: (l: CloseReportLine) => l.target_name,
    meta: { cellClass: 'max-w-[22rem]' },
    cell: ({ row }) => <span className="block truncate text-on-surface">{row.original.target_name}</span>,
  },
  {
    id: 'per',
    header: 'Per unit',
    accessorFn: (l: CloseReportLine) => Number(l.quantity_per),
    meta: { align: 'right', numeric: true },
    cell: ({ row }) => formatQty(row.original.quantity_per),
  },
  num('required', 'Needed'),
  num('consumed', 'Used'),
  {
    id: 'unsourced',
    header: 'From no box',
    accessorFn: (l: CloseReportLine) => l.unsourced,
    meta: { align: 'right', numeric: true },
    cell: ({ row }) =>
      row.original.unsourced > 0 ? (
        <span className="text-amber-300">{row.original.unsourced}</span>
      ) : (
        <span className="text-on-surface-variant">0</span>
      ),
  },
  num('remaining_in_assembly', 'Left in Assembly'),
  {
    id: 'short',
    header: 'Short',
    accessorFn: (l: CloseReportLine) => l.short,
    meta: { align: 'right' },
    cell: ({ row }) =>
      row.original.short > 0 ? (
        <Tag size="sm" tone="bad">
          {row.original.short} short
        </Tag>
      ) : (
        <span className="text-on-surface-variant">—</span>
      ),
  },
];

const getLineId = (l: CloseReportLine) => String(l.line_id);

const CloseReportSection: React.FC<{ orderId: string; refreshKey: string }> = ({ orderId, refreshKey }) => {
  const woApi = useWorkOrdersApi();
  const [report, setReport] = useState<CloseReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    woApi
      .closeReport(orderId)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((err) => {
        if (!cancelled) setError(workOrderErrorMessage(err, 'The close report did not load. Reload the page to try again.'));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, refreshKey]);

  const hasMoney = report?.materials_total_cents !== undefined;
  const hasSplit = !!report?.lines.some((l) => l.consumed_flagged !== undefined);
  const columns = useMemo(
    () => {
      if (!hasSplit) return COLUMNS;
      const after = COLUMNS.findIndex((c) => c.id === 'consumed') + 1;
      return [...COLUMNS.slice(0, after), ...SPLIT_COLUMNS, ...COLUMNS.slice(after)];
    },
    [hasSplit],
  );
  const flaggedCents = report?.flagged_materials_cents;
  const shortLines = report?.lines.filter((l) => l.short > 0).length ?? 0;
  const flagged = report?.units.flagged ?? [];

  return (
    <Panel title="Close report" padding="none" className="overflow-hidden">
      <FadeIn key={error ? 'error' : 'report'}>
      {error ? (
        <div className="px-4 pb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : (
        <DataTable<CloseReportLine>
          data={report?.lines ?? []}
          columns={columns}
          getRowId={getLineId}
          on="panel"
          loading={!report}
          loadingContent={
            <div className="space-y-2 py-6" aria-busy="true" aria-label="Loading the close report">
              {[0, 1].map((i) => (
                <Skeleton key={i} className="h-9 w-full rounded" />
              ))}
            </div>
          }
          rowProps={(l) => ({ className: l.short > 0 ? 'bg-red-500/5' : '' })}
          strip={
            report ? (
              <span className="text-xs tabular-nums text-on-surface-variant">
                {report.units.completed} of {report.units.quantity} built
                {report.units.to_go ? `, ${report.units.to_go} never built` : ''}
                {flagged.length ? `, ${flagged.length} flagged off the run` : ''}
                {shortLines ? `, ${shortLines} ${shortLines === 1 ? 'line' : 'lines'} short` : ''}
              </span>
            ) : undefined
          }
          footer={
            report && hasMoney ? (
              <span className="text-xs tabular-nums text-on-surface-variant">
                Materials used {formatCents(report.materials_total_cents)}
                {flaggedCents ? ` (${formatCents(flaggedCents)} of it on flagged drones)` : ''}, average cost per unit{' '}
                {formatCents(report.unit_cost_avg_cents)}
              </span>
            ) : undefined
          }
          empty={<EmptyState variant="area">This run had no parts list lines to report.</EmptyState>}
        />
      )}
      </FadeIn>
      <Reveal show={flagged.length > 0}>
        <div className="px-4 pb-4 pt-3">
          <h3 className="text-sm font-semibold text-on-surface">Flagged off the run</h3>
          <p className="mt-0.5 text-xs text-on-surface-variant">Their parts stay charged to this run.</p>
          <ul className="mt-2 space-y-1 text-sm">
            {flagged.map((u) => (
              <li key={`${u.position}-${u.serial ?? ''}`} className="flex items-baseline gap-3">
                <span className="w-16 shrink-0 tabular-nums text-on-surface-variant">Unit {u.position}</span>
                <span className="shrink-0 font-mono text-on-surface">{u.serial || '—'}</span>
                <span className="min-w-0 flex-1 truncate text-on-surface-variant" title={u.flag_reason || u.reason || undefined}>
                  {u.flag_reason || u.reason || ''}
                </span>
                {u.flagged_at && (
                  <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-on-surface-variant">
                    {formatTimestamp(u.flagged_at, 'MMM D, h:mm A')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </Panel>
  );
};

export default CloseReportSection;
